// memoryCache.ts
type Entry = { v: any; bytes: number; ts: number };

class MemoryLRU {
  private maxBytes: number;

  private map = new Map<string, Entry>();

  private bytes = 0;

  constructor(maxBytes?: number) {
    // adaptive default (rough, safe on mobile)
    const dm = (navigator as any)?.deviceMemory ?? 4;
    const cap = dm >= 8 ? 60 : dm >= 4 ? 40 : 20; // MB
    this.maxBytes = (maxBytes ?? cap) * 1024 * 1024;
  }

  private estimateBytes(val: any): number {
    try {
      return JSON.stringify(val).length;
    } catch {
      return 1024;
    }
  }

  get<T = any>(key: string): T | null {
    const e = this.map.get(key);
    if (!e) return null;
    e.ts = Date.now();
    return e.v as T;
    // NOTE: callers should treat the returned object as immutable.
  }

  set(key: string, val: any, bytes?: number) {
    const size = bytes ?? this.estimateBytes(val);
    const prev = this.map.get(key);
    if (prev) this.bytes -= prev.bytes;
    this.map.set(key, { v: val, bytes: size, ts: Date.now() });
    this.bytes += size;
    this.evict();
  }

  delete(key: string) {
    const e = this.map.get(key);
    if (!e) return;
    this.bytes -= e.bytes;
    this.map.delete(key);
  }

  private evict() {
    if (this.bytes <= this.maxBytes) return;
    // drop least-recently-used until under cap
    const items = Array.from(this.map.entries());
    items.sort((a, b) => a[1].ts - b[1].ts); // oldest first
    for (const [k, e] of items) {
      this.bytes -= e.bytes;
      this.map.delete(k);
      if (this.bytes <= this.maxBytes) break;
    }
  }
}

export const memCache = new MemoryLRU();

// helpers
export const getFromMemory = <T = any>(key: string) => memCache.get<T>(key);
export const setToMemory = (key: string, val: any, bytes?: number) => memCache.set(key, val, bytes);
export const delFromMemory = (key: string) => memCache.delete(key);
